import React, { useRef, useState } from "react";
import Chart from "react-apexcharts";
import { Box } from "@mui/material";
import ApexCharts from 'apexcharts';
import CODE from "../../../../Static/Constants/StatusCodes";
import { ThemeProperties } from "../../../../Theme/ThemeProperties";
import { formatDate } from "../../../../Helper/DatePicker/DateFormatters";
import CartLoader from "../../../../Helper/Loaders/CartLoader";
import { getTruncatedName, toTitleCase } from "../../../../Helper/Formatters/TextFormatter";
import { isBaseDataSeriesEmpty } from "../../../../Helper/DigitalTwin/VitalChartConfigurations";
import ZoomResetView from "../../components/ZoomResetView";
import NoDataOnChart from "../../components/NoDataOnChart";
import ChartActions from "../BatteryVitals/ChartActions";
import DownloadCSV from "../../../../Helper/CSVOperations/DownloadCSV";

// Helper functions

const calculateBounds = (data, xmax, ymin) => {
    data.forEach((item) => {
        xmax = Math.max(xmax, item.x || 0);
        ymin = Math.min(ymin, item.y !== undefined ? item.y : ymin);
    });
    return { xmax, ymin };
};

// Main component
const BatteryHealthChart = ({
    data,
    cycleAgeTypeText,
    cycleAge,
    yTitleColor,
    deviceID,
    selectedXAxisTitle,
    xTitle,
    yTitle,
    searchID,
    searchColumn,

}) => {

    const [showXTitle, setShowXTitle] = useState(false)

    const zoomResetViewRef = useRef(null);

    const chartID = `battery-health-chart-${deviceID}`

    const statusCode = data?.responseStatus?.code;

    const newDegradationData = data.sohData.map((item) => ({
        date: item.date || 0,
        secondaryX: item.secondaryX || 0,
        x: item.x || 0,
        y: item.y || 0,
        presentCapacity: item.presentCapacity,
        sohConfidence: item.sohConfidence ?? null,
    }));

    const closestMultiple = (n, x) => (x > n ? x : Math.ceil(n / x) * x);

    const previousMultiple = (n, x) => (x > n ? x : Math.floor(n / x) * x);

    const { xmax: newXmax, ymin: newYmin } = calculateBounds(data.sohData, 0, 100);
    const { xmax: newXmax2, ymin: newYmin2 } = calculateBounds(data.rulData, newXmax, newYmin);

    const getChartSettings = (isChartEmpty) => {
        return {
            id: chartID,
            zoom: {
                enabled: !isChartEmpty,
                type: 'x',
                allowMouseWheelZoom: false,
            },
            toolbar: { show: false },
            animations: { enabled: false },
            events: {
                zoomed: () => {
                    zoomResetViewRef?.current?.show();
                },
            },
        }
    }

    const getYAxisSettings = () => {
        return {
            min: 60,
            max: 110,
            tickAmount: 6,
            axisBorder: { show: true, color: "#808080", offsetX: -1.5 },
            labels: {
                style: {
                    colors: "#87939D",
                    fontSize: ThemeProperties.c_axis_labels,
                    fontFamily: "Roboto",
                    fontWeight: ThemeProperties.c_axis_labels_weight,
                },
                formatter: (value) => Math.round(value),
            },
            title: {
                text: "State of Health (%)",
                offsetX: 5,
                style: {
                    color: '#87939D',
                    fontSize: ThemeProperties.c_axis_title,
                    fontWeight: ThemeProperties.c_axis_labels_weight,
                    fontFamily: "Roboto",
                },
            },
        };
    };

    const getXAxisSettings = (xMax) => {
        return {
            type: "numeric",
            tooltip: { enabled: false },
            tickAmount: 5,
            tickPlacement: 'on',
            min: 0,
            max: xMax,
            axisBorder: {
                show: true,
                color: ThemeProperties.c_axis_labels_color,
            },
            labels: {
                rotate: 0,
                datetimeUTC: false,
                rotateAlways: false,
                hideOverlappingLabels: true,
                style: {
                    colors: "#87939D",
                    fontSize: "12px",
                    fontFamily: "Roboto",
                    fontWeight: "500",
                    LineHeight: "16px",
                },
                offsetX: 0,
                offsetY: -2,
                formatter: (value) => {
                    return typeof value === "number" ? value.toFixed(0) : value ?? "";
                },
            },
            title: {
                text: showXTitle ? selectedXAxisTitle : undefined,
                offsetX: 3,
                offsetY: -5,
                style: {
                    color: ThemeProperties.black,
                    fontSize: ThemeProperties.c_axis_title,
                    fontFamily: "Roboto",
                    fontWeight: ThemeProperties.c_axis_title_weight,
                },
            },
            axisTicks: {
                show: false,
                height: 3,
                borderType: "solid",
                color: ThemeProperties.c_axis_labels_color,
            },
        };
    };

    const getTooltipSettings = (isChartEmpty, cycleAge, cycleAgeTypeText) => {

        const formatTooltip = ({ seriesIndex, dataPointIndex, w }) => {
            const data = w.globals.initialSeries[seriesIndex].data[dataPointIndex];
            const seriesColor = w.globals.colors[seriesIndex] || '#4D44D8';
            let seriesName = w.globals.seriesNames[seriesIndex]
            const isForecasted = data.isForecasted

            if (!Number.isFinite(data.y)) {
                return "";
            }

            const value = data.y ? parseInt(data.y) + ' %' : 'N/A'
            let eqCycles = cycleAge ? data.x : data.secondaryX;
            const newDate = data.date ? formatDate(new Date(data.date)) : "Unknown";
            const presentCapacity = Number.isFinite(data.presentCapacity)
                ? data.presentCapacity.toFixed(1) + ' Ah'
                : "NA";

            const sohConfidence = data.sohConfidence ? toTitleCase(data.sohConfidence) : "NA";

            let ageInMonths = cycleAge ? data.secondaryX : data.x;

            if (typeof ageInMonths == 'number') {
                ageInMonths = ageInMonths.toFixed(1) + ' months'
            }

            if (typeof eqCycles == 'number') {
                eqCycles = eqCycles.toFixed(0)
            }

            // seriesName = getTruncatedName(seriesName, 6, 2, 3)

            let actualValues = [
                `<div style = "diplay:flex; margin-bottom: 6px;">
                    <span style = "color: #8F8F8F; font-weight: 500; line-height: '12px' ">SOH:</span>
                    <span> <strong>${value ?? 'NA'}</strong> </span>
                </div>`,

                `<div style = "diplay:flex; margin-bottom: 6px;">
                    <span style = "color: #8F8F8F; font-weight: 500; line-height: '12px' ">Age:</span>
                    <span> <strong>${ageInMonths}</strong> </span>
                </div>`,

                `<div style = "diplay:flex; margin-bottom: 6px;">
                        <span style = "color: #8F8F8F; font-weight: 500; line-height: '12px'">BMS Cycles:</span>
                        <span> <strong>${eqCycles}</strong></span>
                </div>`,

                `<div style = "diplay:flex; margin-bottom: 6px;">
                        <span style = "color: #8F8F8F; font-weight: 500; line-height: '12px' ">Present Capacity:</span>
                        <span> <strong>${presentCapacity}</strong></span>
                </div>`,

                `<div style = "diplay:flex; margin-bottom: 6px;">
                        <span style = "color: #8F8F8F; font-weight: 500; line-height: '12px' ">SOH Estimation Confidence:</span>
                        <span> <strong>${sohConfidence}</strong></span>
                </div>`
            ]

            let rulValues = [
                `<div style = "diplay:flex; margin-bottom: 6px;">
                    <span style = "color: #8F8F8F; font-weight: 500; line-height: '12px' ">Predicted SOH:</span>
                    <span><strong>${value}</strong></span>
                </div>`,

                `<div style = "diplay:flex; margin-bottom: 6px;">
                    <span style = "color: #8F8F8F; font-weight: 500; line-height: '12px' ">Predicted Age:</span>
                    <span><strong>${ageInMonths}</strong></span>
                </div>`,

                `<div style = "diplay:flex; margin-bottom: 6px;">
                    <span style = "color: #8F8F8F; font-weight: 500; line-height: '12px' ">Predicted BMS Cycles:</span>
                    <span><strong>${eqCycles}</strong></span>
                </div>`
            ]

            return `
                    <div>
                        <span>
                            <div class="apexcharts-tooltip-title">
                                ${newDate}
                            </div>
                        </span>

                        <div class="tooltip_container" style = "font-size: 12px;">
                            <div style = "display: flex;">
                                <div style = "display:flex; justify-content: center; gap: 8px;">
                                    <span class="dot" style=" margin-top: 2px; background-color: ${seriesColor};"></span>
                                    <span>${seriesName}</span>
                                    <span>:</span>
                                </div>
                                <div style = "padding-left: 8px;" >
                                    ${isForecasted ? rulValues.join('') : actualValues.join('')}
                                </div>
                            </div>
                        </div>
                    </div>
        `;
        };

        return {
            enabled: !isChartEmpty,
            intersect: false,
            followCursor: true,
            fixed: { enabled: false },
            custom: formatTooltip,
        }
    }

    const getChartOptions = (xMax, statusCode, cycleAge, cycleAgeTypeText) => {
        const isChartEmpty = statusCode !== CODE.SUCCESS;
        return {
            colors: [yTitleColor, "#E9E9E9"],
            dataLabels: { enabled: false },
            fill: { opacity: [1, 1] },
            forecastDataPoints: { count: data?.rulData?.length || 0 },
            stroke: { curve: "straight", width: [2.5, 0] },
            markers: { size: 0, hover: { size: 5, sizeOffset: 1 } },
            legend: {
                show: true,
                position: "top",
                showForSingleSeries: true,
                horizontalAlign: 'left',
                // offsetX: -890,
                offsetY: 16,
                itemMargin: {
                    horizontal: 1,
                    vertical: 5
                },
                markers: {
                    size: 5,
                },
            },
            chart: getChartSettings(isChartEmpty),
            yaxis: getYAxisSettings(),
            xaxis: getXAxisSettings(xMax),
            tooltip: getTooltipSettings(isChartEmpty, cycleAge, cycleAgeTypeText)
        };
    };

    const generateSeries = (dataSeries1, dataSeries2, statusCode) => {
        if (statusCode !== CODE.SUCCESS) {
            return [];
        }

        const formattedSeries1 = dataSeries1.map(item => ({ ...item, isForecasted: false }));
        const formattedSeries2 = dataSeries2.map(item => ({ ...item, isForecasted: true }));


        return [
            {
                type: "line",
                name: `Pack 1: ${deviceID}`,
                data: [...formattedSeries1, ...formattedSeries2],
            }
        ];
    };

    const isDataSeriesEmpty = () => {
        return isBaseDataSeriesEmpty(series)
    }

    const getCSVDataForExport = (deviceID, sereisData) => {
        if (!data.sohData) {
            return []
        }

        const formatItem = (item, type, ageKey, sohKey) => ({
            "Serial Number": deviceID,
            "Age (months)": item[ageKey] !== null ? (item[ageKey] / 30).toFixed(2) : "",
            [cycleAgeTypeText]: item.eqCycleEst?.toFixed(2),
            "SOH (%)": item[sohKey]?.toFixed(2),
            "Initial Capacity (Ah)": parseFloat(item?.initialCapacity).toFixed(2),
            "Present Capacity (Ah)": parseFloat(item?.presentCapacity).toFixed(2),
            "Date": item.date ? formatDate(new Date(item.date)) : "Unknown",
            "Type": type
        });


        const csvData = [
            ...data.sohActualResponse?.map(item =>
                formatItem(item, 'SOH', 'age', 'sohEst')
            ),
            ...data.rulActualResponse?.map(item =>
                formatItem(item, 'RUL', 'calendarAgeInDays', 'sohForecast')
            )
        ];

        return csvData
    };

    if (data.responseStatus.code === CODE.LOADING) {
        return <Box sx={{ minHeight: '360px', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
            <CartLoader />
        </Box>
    }

    const series = generateSeries(newDegradationData, data.rulData, statusCode);
    const xMax = closestMultiple(Math.ceil(newXmax2), 5);
    const options = getChartOptions(xMax, statusCode, cycleAge, cycleAgeTypeText);

    const downloadChartAsCSV = async () => {
        try {
            let csvData = getCSVDataForExport(deviceID, series)
            DownloadCSV(`Battery Health ${deviceID}`, csvData);
        } catch (err) {
            console.log(err)
        }
    };

    const downloadChartAsSVG = async () => {
        try {
            setShowXTitle(true)
            setTimeout(() => {
                const chartInstance = ApexCharts.getChartByID(chartID);
                chartInstance.exports.exportToSVG()
                setShowXTitle(false)
            }, 10)

        } catch (error) {
            console.error('Error downloading chart:', error);
        }
    };


    return (
        <Box sx={{
            position: 'relative',
            '& .apexcharts-legend-series': { gap: 0.5 },
            '& .apexcharts-legend': { justifyContent: 'flex-start !important' },
            '& .apexcharts-legend-text': {
                fontFamily: 'Roboto',
                color: '#3D4B5A',
                fontSize: '12px',
                fontWeight: 400
            },
            // '& .apexcharts-tooltip': {
            //     opacity: '1 !important'
            // }

        }}>

            <Box sx={{ position: 'absolute', right: 0, top: '14px', zIndex: 1 }}>
                <ZoomResetView ref={zoomResetViewRef} chartID={chartID} />
            </Box>

            <Box sx={{ position: 'absolute', right: 0, top: '16px', zIndex: 1 }}>
                <ChartActions actions={[
                    { type: 'nothing', label: 'Download CSV', icon: 'download-file-icon', onClick: downloadChartAsCSV },
                    { type: 'nothing', label: 'Download Image', icon: 'download-image-icon', onClick: downloadChartAsSVG },
                ]} />
            </Box>

            <NoDataOnChart
                onClick={() => { }}
                isDataEmpty={isDataSeriesEmpty?.() ?? false}
                disableRedirect={true}
                noDataMessage={"SOHc for this battery is not computed"}
            />

            <Chart
                key={Math.random()}
                options={options}
                series={series}
                height={360}
                type={"line"}
                width={'100%'}
            />
        </Box>
    );
};

export default BatteryHealthChart;
